import { AxiosError } from 'axios';

import LoginController from '@/api/login/login.ts';
import { useAuthStore } from '@/store/useAuthStore.ts';
import { logEvent } from '@/utils/sentry-monitor.tsx';

// Singleton promise to prevent multiple simultaneous refresh requests
let refreshPromise: Promise<string | null> | null = null;

const MAX_REFRESH_RETRIES = 3;
const RETRY_DELAY_MS = 500;

const isNetworkError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    // Status 0 or no response means network error (timeout, aborted, CORS, etc.)
    return !error.response || error.response.status === 0;
  }
  return false;
};

const isAuthError = (error: unknown): boolean => {
  if (error instanceof AxiosError && error.response) {
    const status = error.response.status;
    return status === 401 || status === 403;
  }
  return false;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const checkAndRefreshToken = async (): Promise<string | null> => {
  const { accessToken, refreshToken, accessExpire, setTokens, clearTokens } =
    useAuthStore.getState();

  const currentTime = Math.floor(Date.now() / 1000);

  // Token is still valid - return it immediately
  if (accessToken && accessExpire && currentTime < Number(accessExpire)) {
    return accessToken;
  }

  // No tokens available - nothing to refresh
  if (!accessToken || !refreshToken) {
    return accessToken;
  }

  // If refresh is already in progress - wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start refresh and save the promise so other requests can wait for it
  refreshPromise = (async () => {
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= MAX_REFRESH_RETRIES; attempt++) {
      try {
        // Re-check state in case it was updated while we were waiting
        const currentState = useAuthStore.getState();
        const now = Math.floor(Date.now() / 1000);

        // Token might have been refreshed by another request
        if (
          currentState.accessExpire &&
          now < Number(currentState.accessExpire)
        ) {
          return currentState.accessToken;
        }

        // No tokens to refresh (might have been cleared by logout)
        if (!currentState.accessToken || !currentState.refreshToken) {
          return null;
        }

        const res = await LoginController.refreshToken({
          access_token: currentState.accessToken,
          refresh_token: currentState.refreshToken
        });

        setTokens({
          accessToken: res.token.access_token,
          refreshToken: res.token.refresh_token,
          accessExpire: res.token.access_expire
        });

        return res.token.access_token;
      } catch (error) {
        lastError = error;

        // Auth error (401/403) - refresh token is invalid, clear tokens immediately
        if (isAuthError(error)) {
          logEvent(
            `Refresh token invalid (auth error), clearing tokens`,
            { attempt },
            'warning',
            true
          );
          clearTokens();
          return null;
        }

        // Network error - retry after delay (don't clear tokens)
        if (isNetworkError(error) && attempt < MAX_REFRESH_RETRIES) {
          await sleep(RETRY_DELAY_MS * attempt); // Exponential backoff
          continue;
        }

        // Other errors or last attempt - log but don't clear tokens for network errors
        if (attempt === MAX_REFRESH_RETRIES) {
          const errorInfo =
            lastError instanceof AxiosError
              ? {
                  status: lastError.response?.status,
                  code: lastError.code,
                  message: lastError.message
                }
              : { error: String(lastError) };

          logEvent(
            `Refresh token failed after ${MAX_REFRESH_RETRIES} attempts`,
            errorInfo,
            'warning',
            true
          );

          // Only clear tokens if it's NOT a network error (server explicitly rejected)
          if (!isNetworkError(lastError)) {
            clearTokens();
          }
        }
      }
    }

    return null;
  })();

  try {
    return await refreshPromise;
  } finally {
    // Clear the promise so next refresh can happen
    refreshPromise = null;
  }
};
